import { Button, Flex, Form, Input, Modal, Select, Tooltip } from "antd";
import { AlertType } from "../../providers/types";
import { useLogs } from "../../providers/LogProvider";
import {
  MODE_CREATE,
  MODE_EDIT,
  TYPE_EMAIL,
  TYPE_SLACK,
} from "../../scenes/alerts/consts";
import { useAuth } from "../../providers/AuthProvider";
import { DeleteOutlined } from "@ant-design/icons";
import { useState } from "react";

export const ManageAlert = ({
  handleAlertCreation,
  onClose,
  mode,
  handleDelete,
  activeAlert,
}: {
  handleAlertCreation: (values: {
    appId: string;
    emails: string[];
    text: string;
    channelType: string;
  }) => void;
  onClose: () => void;
  mode?: string;
  handleDelete: () => void;
  activeAlert: AlertType | null;
}) => {
  const { email } = useAuth();
  const { activeApp, apps } = useLogs();
  const [alertType, setAlertType] = useState(
    activeAlert ? activeAlert?.channels[0].channelType.toString() : TYPE_EMAIL,
  );

  const getInitialValues = () => {
    const initialValues: Record<string, string | string[]> = {};
    initialValues.appId = activeAlert?.appId || activeApp;
    initialValues.text = activeAlert?.text || "";

    if (activeAlert?.channels[0].channelType === parseInt(TYPE_SLACK)) {
      initialValues.webhookUrl = activeAlert?.channels[0].params.webhookUrl;
      initialValues.alertType = TYPE_SLACK;
    } else {
      initialValues.emails = activeAlert?.channels[0].params.emails || [email];
      initialValues.alertType = TYPE_EMAIL;
    }

    return initialValues;
  };

  if (!apps) {
    return null;
  }

  return (
    <Modal
      title={mode === MODE_CREATE ? "Create alert" : "Edit alert"}
      open={true}
      onCancel={onClose}
      footer={null}
    >
      <Form
        name="basic"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        style={{ maxWidth: 600, width: "100%" }}
        initialValues={getInitialValues()}
        onFinish={(values) =>
          handleAlertCreation({ ...values, channelType: alertType })
        }
        onFinishFailed={() => console.log("failed")}
        autoComplete="off"
      >
        <Form.Item
          label="Application"
          name="appId"
          rules={[
            {
              required: true,
              message: "Please select your application",
            },
          ]}
        >
          <Select>
            {apps.map((app) => (
              <Select.Option key={app.id} value={app.id}>
                {app.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Tooltip title="Enter a search phrase (case sensitive) to trigger an alert when matched.">
          <Form.Item
            label="Alert phrase"
            name="text"
            rules={[
              {
                required: true,
                message: "Please input your alert message!",
              },
              {
                min: 5,
                max: 40,
                message: "Text lenght should be between 5 to 40 characters",
              },
            ]}
          >
            <Input.TextArea style={{ width: "100%", marginBottom: 5 }} />
          </Form.Item>
        </Tooltip>
        <Form.Item
          label="Choose alert type"
          name="alertType"
          rules={[
            {
              required: true,
              message: "Choose alert type",
            },
          ]}
        >
          <Select
            style={{ width: "100%", marginBottom: 5 }}
            placeholder="Choose alert type"
            value={alertType}
            onChange={(value) => setAlertType(value)}
            options={[
              {
                value: TYPE_EMAIL,
                label: "E-mail",
              },
              {
                value: TYPE_SLACK,
                label: "Slack",
              },
            ]}
          />
        </Form.Item>

        {alertType === TYPE_SLACK && (
          <Form.Item
            label="Webhook URL"
            name="webhookUrl"
            rules={[
              {
                required: true,
                message: "Please input your webhook URL!",
              },
            ]}
          >
            <Input placeholder="https://hooks.slack.com/services/..." />
          </Form.Item>
        )}
        {alertType === TYPE_EMAIL && (
          <Form.List name="emails">
            {(fields, { add, remove }) => {
              return (
                <div>
                  {fields.map((field, index) => (
                    <Form.Item
                      label={index === 0 ? "E-mail" : ""}
                      required={true}
                      key={field.key}
                      {...(index === 0
                        ? {}
                        : {
                            wrapperCol: {
                              sm: { span: 16, offset: 8 },
                            },
                          })}
                    >
                      <Flex>
                        <Form.Item
                          {...field}
                          validateTrigger={["onChange", "onBlur"]}
                          rules={[
                            {
                              required: true,
                              message: "Please input your email!",
                            },
                          ]}
                          noStyle
                        >
                          <Input
                            placeholder="e-mail address"
                            style={{ width: "100%", marginBottom: 5 }}
                          />
                        </Form.Item>
                        {fields.length > 1 ? (
                          <DeleteOutlined
                            style={{ margin: "0 8px" }}
                            onClick={() => {
                              remove(field.name);
                            }}
                          />
                        ) : null}
                      </Flex>
                    </Form.Item>
                  ))}
                  {fields.length < 5 && (
                    <Form.Item wrapperCol={{ sm: { offset: 8 } }}>
                      <Button
                        type="dashed"
                        onClick={() => {
                          add();
                        }}
                        style={{ width: "100%" }}
                      >
                        Add e-mail
                      </Button>
                    </Form.Item>
                  )}
                </div>
              );
            }}
          </Form.List>
        )}

        <Flex
          style={{ marginTop: 40 }}
          justify={mode === MODE_EDIT ? "space-between" : "flex-end"}
        >
          {mode === MODE_EDIT && (
            <Button danger onClick={handleDelete}>
              Delete
            </Button>
          )}

          <Button type="primary" form="basic" key="submit" htmlType="submit">
            {mode === MODE_CREATE ? "Create" : "Save"}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
